/* eslint-disable react-refresh/only-export-components -- intentional: this module exports
   the provider alongside its context hooks (useActiveMatchContext, useActiveBackgammonMatch,
   useActiveQcpMatch) so downstream screens (T4-T7) import them all from one path, per the
   feature plan (docs/superpowers/plans/2026-07-21-active-match-pill.md). */
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  useBackgammonSocket,
  type BackgammonSocket,
} from '@qlashgeminiz/qgammon-board-ui';

import { env } from '@/env';
import { qgammonSocketAuth } from '@/lib/qgammon/auth';
import { QGAMMON_SOCKET_OPTIONS } from '@/lib/qgammon/socketOptions';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearActiveMatch, coloreDalServer, partitaFinita } from '@/store/activeMatch.slice';
import { useQcpMatch, type QcpMatchSocket } from '@/hooks/useQcpMatch';
import {
  projectQgammon,
  projectQcp,
  type ActiveMatchProjection,
} from '@/lib/activeMatch/projection';
import type { ActiveMatchDescriptor } from '@/store/activeMatch.slice';
import { avvisoPrimaDellaResa, statoAutoplayDa } from '@/lib/backgammon/autoplay';
import { CoachContext } from '@/lib/qgammon/coachContext';
import { useCronologiaPartita } from '@/lib/qgammon/useCronologia';
import {
  useCoachComando,
  useCoachFeed,
  type CoachDelTavolo,
  type FeedCoach,
} from '@/lib/qgammon/useCoachQgammon';

interface ActiveMatchContextValue {
  descriptor: ActiveMatchDescriptor | null;
  qgammon: BackgammonSocket;
  qcp: QcpMatchSocket;
  projection: ActiveMatchProjection | null;
  /** Resign the currently-active match (used when replacing it with a new one). */
  resignActive: () => void;
  /**
   * ADR-0234 §4(e) con l'errata (8): la partita attiva e' un backgammon con atti in autoplay (`withAutoplay` dello snapshot) E il flag della TMA e' acceso,
   * quindi abbandonarla con la resa del MATCH la toglie dai numeri di chi la lascia: chi sta per farlo da FUORI dalla schermata (la X della pastiglia,
   * il «hai gia' una partita in corso» di `useStartMatch`) deve leggere la stessa frase che la schermata di gioco mostra fra i due tocchi. Falso
   * senza partita, per il burraco/QCP, a flag spento e finche' lo snapshot non lo dice. Opzionale: i doppi di test non lo hanno, e `undefined` vale falso.
   */
  resaSenzaNumeri?: boolean;
  /**
   * Rimonta il socket QGammon (matchId X → null → X): board-ui non espone un `retry()`, un
   * handshake rifiutato non si ritenta mai da solo (v. `socketOptions.ts`), quindi il «Riprova»
   * lo fa la TMA riavviando l'effetto del hook (dipendenze `[wsUrl, matchId, autoJoin]`). `myColor`
   * resta: nel passaggio X→null→X il posto non cambia (README board-ui).
   */
  riprovaConnessioneQgammon: () => void;
}

/**
 * ⛔ IL FLAG DEL COACH SI LEGGE SOLO COSI': il confronto LETTERALE `import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true'`, in QUESTO
 * modulo, nel punto che sceglie il ramo. NON una costante esportata (rolldown decide gli export fra chunk PRIMA di piegarla, e la build di
 * produzione manterrebbe il modulo: overview TMA, «Due lezioni di BUILD e di BANCO», (a)). In produzione il ramo vero sparisce e con lui
 * `lib/qgammon/useCoachQgammon.ts` (la mappa `listeners`, la chiamata `command('coach:set'…)`).
 * I due «spenti» sono hook veri (si chiamano sempre, nello stesso ordine): non cambiano il numero di hook fra una build e l'altra.
 */
const useFeedCoach: (matchId: string | null) => FeedCoach | null =
  import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' ? useCoachFeed : () => null;
function useComandoCoachVero(feed: FeedCoach | null, sock: BackgammonSocket): CoachDelTavolo | null {
  // `feed` non e' mai null qui: il ramo vero e' scelto dallo stesso letterale che sceglie `useFeedCoach`.
  const comando = useCoachComando(feed as FeedCoach, sock);
  // LA CRONOLOGIA (D5 della revisione di #957): il riduttore sta QUI, nel provider che sopravvive alla navigazione, e non nella rotta: chi esce
  // dal tavolo e rientra senza una nuova `match:join` ritrova i turni. Fonti: `match:history` (dal feed) e `lastMove`/`lastRoll` del socket.
  const cronologia = useCronologiaPartita({
    storia: (feed as FeedCoach).stato.storia,
    lastMove: sock.lastMove,
    lastRoll: sock.lastRoll,
    matchId: sock.state?.matchId ?? null,
    gameNumber: sock.state?.gameNumber ?? 1,
  });
  return useMemo(() => ({ ...comando, cronologia }), [comando, cronologia]);
}
const useComandoCoach: (feed: FeedCoach | null, sock: BackgammonSocket) => CoachDelTavolo | null =
  import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' ? useComandoCoachVero : () => null;

const ActiveMatchContext = createContext<ActiveMatchContextValue | null>(null);

/**
 * Owns the live match socket ABOVE the route tree so it survives navigation
 * (the server forfeits on disconnect, not on turn — see the spec). Both game
 * hooks are mounted unconditionally; only the active game's matchId is passed,
 * the other stays null (no socket). Clears the slice centrally when the match
 * ends so the socket tears down cleanly.
 */
export function ActiveMatchProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const descriptor = useAppSelector((s) => s.activeMatch.current);

  // Il «Riprova» dopo un handshake rifiutato (§D.3 del piano board-ui 0.8): board-ui non ha un
  // `retry()`, quindi si rimonta il socket passando il matchId per `null` un giro e tornando al
  // valore vero il giro dopo. `riprovando` lo forza: mentre e' vero il hook vede `matchId: null`
  // (si smonta il socket rifiutato), l'effetto sotto lo rimette a `false` al render successivo
  // (si rimonta con l'auth riletta da capo).
  const [riprovando, setRiprovando] = useState(false);
  useEffect(() => {
    if (riprovando) setRiprovando(false);
  }, [riprovando]);
  const qgammonMatchId =
    descriptor?.game === 'qgammon' && !riprovando ? descriptor.matchId : null;

  // The shared hook is surface-agnostic: the TMA supplies the WS origin and the
  // handshake auth. `getAuth` (not `getAccessToken`) because the TMA's
  // resolution order is richer than "a Logto token" — per-user Logto token
  // first, else the dev-bypass fallback, else nothing (see lib/qgammon/auth.ts).
  const coachFeed = useFeedCoach(qgammonMatchId);
  const qgammon = useBackgammonSocket({
    wsUrl: env.qgammonWsUrl,
    matchId: qgammonMatchId,
    getAuth: qgammonSocketAuth,
    // 🔴 CORRETTO 2026-09-28 (giro board-ui 0.8, reperto 3): la riga precedente diceva che
    // Socket.IO "risponde a un handshake rifiutato riconnettendosi" — falso, letto nel sorgente
    // di socket.io-client 4.8.3 installato (`socket.js`): su un pacchetto `CONNECT_ERROR` la
    // libreria chiama `destroy()` e SOLO DOPO emette `connect_error`, quindi un handshake
    // rifiutato non si ritenta MAI, con qualunque `reconnection*` (v. `socketOptions.ts` per il
    // dettaglio). Il vero motivo di questi valori — e del backoff separato di board-ui ≥0.7.0 per
    // `too_many_connections` — resta lo stesso budget del gameserver: v. `socketOptions.ts`.
    socketOptions: QGAMMON_SOCKET_OPTIONS,
    // Il coach (board-ui 0.17.0): la mappa nasce SOLO nel ramo piegato. A flag spento il config e' quello di prima, senza la chiave `listeners`.
    ...(import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' && coachFeed
      ? { listeners: coachFeed.listeners }
      : {}),
  });
  const coach = useComandoCoach(coachFeed, qgammon);
  const qcp = useQcpMatch(descriptor?.game === 'qcp' ? descriptor.matchId : null, {
    vsBot: descriptor?.vsBot,
  });

  // 🔴 Il colore dell'ack di `match:join` (board-ui 0.8.0, `myColor`) — per il colore alla
  // RICARICA durante la partita (reperto 3). Scrive nello slice solo quando la partita attiva e'
  // ANCORA questa e il colore che conosciamo diverge da quello che il server ha appena confermato:
  // il reducer stesso ripete il confronto sul `matchId` (difesa in profondita', v. lo slice), ma
  // farlo anche qui evita un `dispatch` a ogni render quando i due gia' concordano.
  useEffect(() => {
    if (
      descriptor?.game === 'qgammon' &&
      qgammon.myColor &&
      descriptor.perspective !== qgammon.myColor
    ) {
      dispatch(coloreDalServer({ matchId: descriptor.matchId, color: qgammon.myColor }));
    }
  }, [descriptor, qgammon.myColor, dispatch]);

  // Clear the slice only when the ACTIVE match reaches its terminal signal —
  // and only if we've seen THAT match id live first. A game hook mounted across
  // matches can briefly still expose the previous match's terminal payload on
  // the first render of the next match; the liveRef guard ignores that stale
  // signal instead of wiping the fresh match.
  const liveRef = useRef<string | null>(null);
  useEffect(() => {
    if (!descriptor) return;
    const terminal =
      descriptor.game === 'qgammon' ? !!qgammon.ended : !!qcp.matchResult;
    if (!terminal) {
      liveRef.current = descriptor.matchId; // this match is live now
      return;
    }
    if (liveRef.current === descriptor.matchId) {
      // Prima di svuotare, si ricorda QUALE partita e' finita: ne' l'azione persistente ne' la scheda
      // d'incontro (`useTavoliFiniti`, #408) devono riproporre il tavolo di un incontro Limulo che il
      // server chiude solo dopo (~45 s).
      if (descriptor.game === 'qgammon') dispatch(partitaFinita(descriptor.matchId));
      dispatch(clearActiveMatch());
    }
  }, [descriptor, qgammon.ended, qcp.matchResult, dispatch]);

  const projection: ActiveMatchProjection | null = useMemo(() => {
    if (!descriptor) return null;
    return descriptor.game === 'qgammon'
      ? projectQgammon(qgammon, descriptor)
      : projectQcp(qcp);
  }, [descriptor, qgammon, qcp]);

  const value = useMemo<ActiveMatchContextValue>(
    () => ({
      descriptor,
      qgammon,
      qcp,
      projection,
      resignActive: () => {
        if (descriptor?.game === 'qgammon') qgammon.resign(1, 'match');
        else if (descriptor?.game === 'qcp') qcp.resign();
      },
      // ⚠️ `statoAutoplayDa` legge `withAutoplay` col ripiego di un socket che non lo manda; il `resign(1, 'match')` qui sopra e' LA resa del match.
      resaSenzaNumeri:
        descriptor?.game === 'qgammon' &&
        avvisoPrimaDellaResa(env.featureBackgammonAutoplayEnabled, statoAutoplayDa(qgammon).conAtti),
      riprovaConnessioneQgammon: () => setRiprovando(true),
    }),
    [descriptor, qgammon, qcp, projection],
  );

  // Il coach ha il SUO contesto (`lib/qgammon/coachContext.ts`): `null` a flag spento, sempre in produzione.
  return (
    <ActiveMatchContext.Provider value={value}>
      <CoachContext.Provider value={coach}>{children}</CoachContext.Provider>
    </ActiveMatchContext.Provider>
  );
}

export function useActiveMatchContext(): ActiveMatchContextValue {
  const ctx = useContext(ActiveMatchContext);
  if (!ctx) throw new Error('useActiveMatchContext must be used within ActiveMatchProvider');
  return ctx;
}

/**
 * Come `useActiveMatchContext`, ma `null` FUORI dal provider invece di lanciare. Per chi vive anche nei banchi che montano il
 * componente da solo (la `TabBar`, I-392): in app il provider c'e' sempre (`AppShell`), quindi li' non cambia nulla.
 */
export function useActiveMatchOpzionale(): ActiveMatchContextValue | null {
  return useContext(ActiveMatchContext);
}

/** Backgammon match socket for the active match (screen consumer). */
export function useActiveBackgammonMatch(): BackgammonSocket {
  return useActiveMatchContext().qgammon;
}

/** QCP match socket for the active match (screen consumer). */
export function useActiveQcpMatch(): QcpMatchSocket {
  return useActiveMatchContext().qcp;
}

/**
 * Il «Riprova» dopo un handshake QGammon rifiutato — hook a SE', non un campo in piu' su
 * `useActiveBackgammonMatch()`: il finto del banco a pixel espone solo quell'hook
 * (`tools/banco-pixel/finti/activeMatchContext.tsx`), e un nuovo campo obbligherebbe a
 * riscriverlo per ogni banco che non ne ha bisogno.
 */
export function useRiprovaConnessione(): () => void {
  return useActiveMatchContext().riprovaConnessioneQgammon;
}
